import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check, Brain, History, Calculator,
  Pill, Clock, LayoutGrid, FlaskConical, Wand2,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/smart-medicine-composer';
const SHARE_MESSAGE = (
  `DocSite's Digital Health File composer just got radically faster.\n\n` +
  `• Type a brand name → molecule, drug class, schedule (H/H1/OTC) and default strength auto-fill (AI)\n` +
  `• Your dosing habits per brand are remembered — next time you type "Crocin", your usual 1-0-1 · 5 days loads instantly\n` +
  `• Total quantity auto-computed from dose × frequency × duration\n` +
  `• All 12 pharmacy form types (TAB, CAP, SYRUP, INJ…) one tap away\n` +
  `• Meal timing chips flow into the printed prescription as clear patient instructions\n\n` +
  `Every field editable — your edits are what print on the Rx.\n\n` +
  `Read more: ${SHARE_URL}`
);

const FEATURES = [
  { icon: Brain, title: 'AI molecule + drug class + schedule lookup', body: 'Type a brand — "Crocin", "Amoxil", "Azithral" — and AI fills the molecule (Paracetamol / Amoxicillin / Azithromycin), therapeutic class (Analgesic · Antipyretic, Antibiotic - Penicillin), Indian regulatory schedule (OTC / H / H1 / X) and default strength. All under a second. All editable — you override, your edit prints on the Rx.', color: 'violet' },
  { icon: History, title: 'Per-doctor medicine memory', body: 'Every prescription you write silently records your dosing habits per brand. Next time you type the same brand, your usual dose, frequency, duration, form-type and meal timings auto-load. Feels like your own personal EMR, learning your style.', color: 'sky' },
  { icon: Calculator, title: 'Auto-computed total quantity', body: '1 tab × 1-0-1 × 5 days = 10 tab. 10ml × 1-1-1 × 5 days = 150 ml. The composer parses your inputs and fills the quantity cell automatically, so patients know exactly what to buy at the pharmacy. Editable — your override wins.', color: 'emerald' },
  { icon: LayoutGrid, title: 'All 12 form-types visible always', body: 'TAB (default), CAP, SYRUP, INJ, DROP, OINTMENT·GEL, OINTMENT·CREAM, SPRAY, GARGLE, RINSE SACHETS, WASH, IV — every form-type is a single tap. No modal, no picker, no "Set form type" button. Persists across every device and every session.', color: 'amber' },
  { icon: Clock, title: 'Meal timings → readable instructions', body: 'Tap AB (After Breakfast) + AD (After Dinner) in the composer, and the Rx prints "After breakfast, After dinner" in the Instructions column. Any free-text notes you add get appended. Pharmacists and patients read plain English, not codes.', color: 'rose' },
  { icon: Pill, title: 'Form-type flows onto the printed Rx', body: 'The Brand column now shows "TAB · Crocin", "SYRUP · Ambroxol", "CAP · Amoxil" — so pharmacists spot the form factor at a glance. Zero ambiguity about tablet vs syrup vs capsule for the same molecule.', color: 'violet' },
  { icon: FlaskConical, title: 'Everything editable, everything remembered', body: 'AI is a starting point, not the final word. Every auto-filled field is editable. Your edits are saved back into your medicine memory — so next time you prescribe the same brand, your version loads, not the AI\'s.', color: 'emerald' },
  { icon: Wand2, title: 'Zero learning curve', body: 'No new menus. No new buttons. No new workflow. Just type medicine names like before — DocSite quietly makes them richer, faster, and smarter. Your first ten prescriptions with the new composer will feel like your last hundred took twice as long.', color: 'sky' },
];

const COLOR_MAP = {
  sky:     { bg: 'bg-sky-100',     border: 'border-sky-200',     icon: 'text-sky-700' },
  emerald: { bg: 'bg-emerald-100', border: 'border-emerald-200', icon: 'text-emerald-700' },
  violet:  { bg: 'bg-violet-100',  border: 'border-violet-200',  icon: 'text-violet-700' },
  amber:   { bg: 'bg-amber-100',   border: 'border-amber-200',   icon: 'text-amber-700' },
  rose:    { bg: 'bg-rose-100',    border: 'border-rose-200',    icon: 'text-rose-700' },
};

function FeatureCard({ f }) {
  const c = COLOR_MAP[f.color] || COLOR_MAP.sky;
  const Ico = f.icon;
  return (
    <div className={`rounded-2xl bg-white border ${c.border} p-5 flex gap-4 items-start hover:shadow-sm transition-shadow`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
        <Ico size={20} className={c.icon} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
      </div>
    </div>
  );
}

export default function SmartMedicineComposerLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="smart-composer-launch-page">
      <Helmet>
        <title>Smart Medicine Composer — AI + memory + auto-quantity | DocSite</title>
        <meta name="description" content="Type a brand name — DocSite AI auto-fills molecule, drug class, schedule, default strength. Remembers your dosing habits per brand. Auto-computes total quantity." />
        <meta property="og:title" content="DocSite — Smart Medicine Composer" />
        <meta property="og:description" content="Every field a doctor writes on a prescription, made 10x faster by AI + memory + math." />
        <meta property="og:url" content={SHARE_URL} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        <div className="rounded-3xl bg-gradient-to-br from-violet-50 via-white to-sky-50 border border-violet-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-violet-600 text-white">
            <Sparkles size={10} /> Just Launched · 4 Aug 2026
          </span>
          <div className="mt-5 flex items-center gap-4">
            <span className="inline-flex w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-violet-600 via-sky-500 to-emerald-500 shadow-lg items-center justify-center">
              <Brain size={40} className="text-white" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700/80">Smart Composer · What&apos;s new</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">AI · Memory · Auto-quantity · Instant form-type</p>
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The Digital Health File composer just got radically faster.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed">
            Type a brand name — DocSite auto-fills the molecule, drug class, regulatory schedule and default strength.
            Remembers your dosing habits per brand. Auto-computes total quantity. Every field editable, every edit
            saved into your personal medicine memory for next time.
          </p>
        </div>

        <div className="space-y-3">
          {FEATURES.map((f, idx) => (<div key={idx} data-testid={`feature-${idx}`}><FeatureCard f={f} /></div>))}
        </div>

        <div className="mt-8 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-2 border-emerald-200 p-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest">
            <Pill size={11} /> See it on the printed Rx
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Every enhancement flows straight onto the prescription printout.
          </h2>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed max-w-2xl">
            Take a look at the sample DocSite prescription — molecule column populated, drug class + schedule visible,
            quantity auto-filled, meal timings translated to plain-English instructions, form-type on every brand row.
          </p>
          <Link to="/whats-new/auto-prescriptions" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
            View sample prescription
          </Link>
        </div>

        <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 sm:p-8">
          <p className="text-[10px] font-bold uppercase tracking-widest text-violet-300">Spread the word</p>
          <h3 className="text-xl sm:text-2xl font-semibold mt-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Know a doctor who is fed up of writing the same thing 40 times a day?
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold" data-testid="share-whatsapp">
              <Share2 size={14} /> Share on WhatsApp
            </a>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold border border-slate-700" data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-10">Published 4 August 2026 · DocSite Smart Medicine Composer v1</p>
      </div>
    </div>
  );
}
